<template>
  <div ref="mapEl" class="map-container" :style="{ height }"></div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// leaflet 기본 마커 아이콘 경로 설정
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl:       require("leaflet/dist/images/marker-icon.png"),
  shadowUrl:     require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "MapDistance",
  props: {
    /** v-model:pointA / v-model:pointB 로 좌표 전달 ( [lat, lng] ) */
    pointA: { type: Array, default: null },
    pointB: { type: Array, default: null },

    /** 초기 보정용 중심과 줌 (둘 다 없으면 점들로 fitBounds) */
    center: { type: Array, default: () => [35.8714354, 128.601445] },
    zoom:   { type: Number, default: 13 },

    /** UI/동작 옵션 */
    height:         { type: String, default: "50vh" },
    draggable:      { type: Boolean, default: false },   // 마커 드래그 허용
    clickable:      { type: Boolean, default: true },    // 지도 클릭으로 점 설정
    fitOnUpdate:    { type: Boolean, default: true },    // 좌표 갱신 시 화면 자동 맞춤
    dash:           { type: String,  default: "6,6" },   // 선 점선 패턴
    pointAName: { type: String, default: "A" },
    pointBName: { type: String, default: "B" },
  },
  emits: ["update:pointA", "update:pointB"],
  data() {
    return {
      map: null,
      markerA: null,
      markerB: null,
      line: null,
      label: null,
      tileLayer: null,
    };
  },
  mounted() {
    this.initMap();
    this.renderAll();
  },
  beforeUnmount() {
    if (this.map) this.map.remove();
  },
  watch: {
    pointA: { deep: true, handler() { this.renderAll(); } },
    pointB: { deep: true, handler() { this.renderAll(); } },
  },
  methods: {
    initMap() {
      // 1) 기본 attribution 컨트롤 비활성
      this.map = L.map(this.$refs.mapEl, { 
        zoomControl: true,
        attributionControl: false
      });

      // 2) 타일 레이어 (OSM 출처)
      this.tileLayer = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(this.map);

      // 3) prefix 없이 Attribution 컨트롤 다시 추가 (Leaflet 링크 제거)
      L.control.attribution({ prefix: false }).addTo(this.map);

      // 초기 뷰
      if (this.pointA && this.pointB) {
        const b = L.latLngBounds([this.pointA, this.pointB]);
        this.map.fitBounds(b, { padding: [40, 40] });
      } else {
        this.map.setView(this.center, this.zoom);
      }
    },
    makeMarker(latlng, label, name) {
      const icon = L.divIcon({
        className: "name-label",
        html: `<div class="name-chip">${name || label}</div>`,
        iconSize: [0, 0],
      });
      return L.marker(latlng, { icon, draggable: this.draggable }).addTo(this.map);
    },

    isValidPoint(p) {
    return Array.isArray(p) && p.length === 2
      && Number.isFinite(p[0]) && Number.isFinite(p[1]);
  },

  renderAll() {
    const hasA = this.isValidPoint(this.pointA);
    const hasB = this.isValidPoint(this.pointB);

    // A 마커 업데이트/제거
    if (hasA) {
      const a = L.latLng(this.pointA[0], this.pointA[1]);
      if (!this.markerA) this.markerA = this.makeMarker(a, "A", this.pointAName);
      else this.markerA.setLatLng(a);
    } else {
      if (this.markerA) { this.map.removeLayer(this.markerA); this.markerA = null; }
    }

    // B 마커 업데이트/제거
    if (hasB) {
      const b = L.latLng(this.pointB[0], this.pointB[1]);
      if (!this.markerB) this.markerB = this.makeMarker(b, "B", this.pointBName);
      else this.markerB.setLatLng(b);
    } else {
      if (this.markerB) { this.map.removeLayer(this.markerB); this.markerB = null; }
    }

    // 선/라벨
    if (hasA && hasB) {
      const a = L.latLng(this.pointA[0], this.pointA[1]);
      const b = L.latLng(this.pointB[0], this.pointB[1]);
      const latlngs = [a, b];

      if (!this.line) {
        this.line = L.polyline(latlngs, {
          color: "#2563eb",
          weight: 3,
          opacity: 0.9,
          dashArray: this.dash,
        }).addTo(this.map);
      } else {
        this.line.setLatLngs(latlngs);
      }

      const dist = a.distanceTo(b);
      const text = this.formatDistance(dist);
      const mid = L.latLng((a.lat + b.lat) / 2, (a.lng + b.lng) / 2);
      const icon = L.divIcon({
        className: "distance-label",
        html: `<div class="distance-chip">${text}</div>`,
        iconSize: [0, 0],
      });

      if (!this.label) {
        this.label = L.marker(mid, { interactive: false, icon }).addTo(this.map);
      } else {
        this.label.setLatLng(mid);
        this.label.setIcon(icon);
      }

      if (this.fitOnUpdate) {
        const bounds = L.latLngBounds(latlngs);
        this.map.fitBounds(bounds, { padding: [40, 40] });
      }
    } else {
      // 한쪽이라도 없으면 선/라벨 제거
      if (this.line)  { this.map.removeLayer(this.line);  this.line  = null; }
      if (this.label) { this.map.removeLayer(this.label); this.label = null; }

      // 보기 위치
      if (hasA) this.map.setView(this.pointA, this.map.getZoom());
      else if (hasB) this.map.setView(this.pointB, this.map.getZoom());
      else this.map.setView(this.center, this.zoom);
    }
  }
  },
};
</script>

<style scoped>
.map-container {
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,.1);
}

::v-deep .distance-label.leaflet-div-icon {
  background: transparent;
  border: 0;
  width: auto !important;
  height: auto !important;
}
::v-deep .distance-chip {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 6px 10px;
  font-size: 13px;
  line-height: 1;
  color: #fff;
  background: rgba(17, 24, 39, 0.85);
  border-radius: 9999px;
  box-shadow: 0 2px 8px rgba(0,0,0,.25);
  transform: translate(-50%, -12px);
}

::v-deep .name-label.leaflet-div-icon {
  background: transparent;
  border: 0;
}
::v-deep .name-chip {
  display: inline-flex;
  transform: translate(-50%, -30px);
  background: rgba(37, 99, 235, 0.9);
  color: #fff;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
</style>
